import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AOS from 'aos';
import 'aos/dist/aos.css';

// Import icons from heroicons
import {
  LightBulbIcon,
  DocumentTextIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';

const features = [
  {
    title: "Capture Everything",
    description: "Save articles, ideas, and inspirations in one secure place. Never lose an important thought again.",
    icon: DocumentTextIcon,
  },
  {
    title: "Smart Organization",
    description: "Our AI-powered system helps you organize and connect your notes automatically.",
    icon: LightBulbIcon,
  },
  {
    title: "Instant Search",
    description: "Find any note in milliseconds with our powerful search engine.",
    icon: MagnifyingGlassIcon,
  },
  {
    title: "Secure & Private",
    description: "Your data is encrypted and stored securely. Your thoughts remain yours alone.",
    icon: ShieldCheckIcon,
  },
];

const testimonials = [
  {
    name: "Sarah Chen",
    role: "Product Designer",
    content: "SnapNote has transformed how I organize my research and design inspiration. It's become an essential part of my creative process.",
    avatar: "https://randomuser.me/api/portraits/women/1.jpg",
  },
  {
    name: "James Wilson",
    role: "Content Creator",
    content: "The search functionality is incredible. I can find any piece of content in seconds, which has made my content creation workflow so much smoother.",
    avatar: "https://randomuser.me/api/portraits/men/1.jpg",
  },
  {
    name: "Emily Rodriguez",
    role: "Researcher",
    content: "As an academic, I need a reliable system to organize my research. SnapNote has exceeded all my expectations.",
    avatar: "https://randomuser.me/api/portraits/women/2.jpg",
  },
];

const LandingPage: React.FC = () => {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    AOS.init({
      duration: 1000,
      once: true,
    });
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center" data-aos="fade-up">
            <h1 className="text-4xl tracking-tight font-extrabold text-gray-900 dark:text-white sm:text-5xl md:text-6xl">
              <span className="block">Your Second Brain for</span>
              <span className="block text-brand-teal">Better Thinking</span>
            </h1>
            <p className="mt-3 max-w-md mx-auto text-base text-gray-500 dark:text-gray-400 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
              Capture, organize, and connect your ideas effortlessly. Transform your scattered thoughts into a powerful knowledge base.
            </p>
            <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8">
              <div className="rounded-md shadow">
                <Link
                  to={isAuthenticated ? "/notes" : "/register"}
                  className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-brand-teal hover:bg-brand-yellow hover:text-brand-navy transition-colors duration-200 md:py-4 md:text-lg md:px-10"
                >
                  Start Building Your Second Brain
                  <ArrowRightIcon className="ml-2 h-5 w-5" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center" data-aos="fade-up">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              Everything you need to think better
            </h2>
            <p className="mt-4 text-lg text-gray-500 dark:text-gray-400">
              Powerful features to help you capture, organize, and grow your knowledge.
            </p>
          </div>

          <div className="mt-20">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className="pt-6"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="flow-root rounded-lg bg-gray-50 dark:bg-gray-900 px-6 pb-8">
                    <div className="-mt-6">
                      <div>
                        <span className="inline-flex items-center justify-center p-3 bg-brand-teal rounded-md shadow-lg">
                          <feature.icon className="h-6 w-6 text-white" aria-hidden="true" />
                        </span>
                      </div>
                      <h3 className="mt-8 text-lg font-medium text-gray-900 dark:text-white tracking-tight">
                        {feature.title}
                      </h3>
                      <p className="mt-5 text-base text-gray-500 dark:text-gray-400">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Demo Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center" data-aos="fade-up">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              SnapNote Preview
            </h2>
            <p className="mt-4 text-lg text-gray-500 dark:text-gray-400">
              Unlock your productivity and creativity—SnapNote brings your ideas to life, keeps you organized, and helps you achieve more every day.
            </p>
          </div>
          <div className="mt-12 flex justify-center" data-aos="zoom-in">
            <img
              src="/demo-screenshot.png"
              alt="SnapNote Preview"
              className="rounded-lg shadow-xl max-w-full h-auto border border-gray-200 dark:border-gray-700"
              style={{ maxHeight: '500px' }}
            />
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center" data-aos="fade-up">
            <h2 className="text-3xl font-extrabold text-gray-900 dark:text-white sm:text-4xl">
              Loved by knowledge workers
            </h2>
            <p className="mt-4 text-lg text-gray-500 dark:text-gray-400">
              Here's what our users have to say about SnapNote.
            </p>
          </div>
          <div className="mt-20 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.name}
                className="bg-gray-50 dark:bg-gray-900 rounded-lg p-8"
                data-aos="fade-up"
                data-aos-delay={index * 100}
              >
                <div className="flex items-center">
                  <img
                    className="h-12 w-12 rounded-full"
                    src={testimonial.avatar}
                    alt={testimonial.name}
                  />
                  <div className="ml-4">
                    <h4 className="text-lg font-bold text-gray-900 dark:text-white">
                      {testimonial.name}
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {testimonial.role}
                    </p>
                  </div>
                </div>
                <p className="mt-6 text-base text-gray-500 dark:text-gray-400">
                  "{testimonial.content}"
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-brand-teal">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center" data-aos="fade-up">
            <h2 className="text-3xl font-extrabold text-white sm:text-4xl">
              Ready to transform your thinking?
            </h2>
            <p className="mt-4 text-xl text-teal-100">
              Join thousands of users who are already building their second brain.
            </p>
            <div className="mt-8">
              <Link
                to={isAuthenticated ? "/notes" : "/register"}
                className="inline-flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-brand-teal bg-white hover:bg-gray-50 transition-colors duration-200 md:py-4 md:text-lg md:px-10"
              >
                Get Started Now
                <ArrowRightIcon className="ml-2 h-5 w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-gray-800">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <nav className="flex flex-wrap justify-center space-x-6">
            <Link to="/about" className="text-base text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
              About
            </Link>
            <Link to="/privacy" className="text-base text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
              Privacy Policy
            </Link>
            <Link to="/terms" className="text-base text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white">
              Terms
            </Link>
          </nav>
          <div className="mt-8 flex justify-center space-x-6">
            {/* Social Media Icons */}
            <a href="https://twitter.com/snapnote" className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <span className="sr-only">Twitter</span>
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
              </svg>
            </a>
            <a href="https://github.com/snapnote" className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
              <span className="sr-only">GitHub</span>
              <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
              </svg>
            </a>
          </div>
          <p className="mt-8 text-center text-base text-gray-400">
            &copy; {new Date().getFullYear()} SnapNote. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage; 